import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DataChangedBanner } from '@/components/DataChangedBanner';
import { useAuthStore } from '@/stores/authStore';
import { startEditing as announceEditing, stillEditing, stopEditing, useTaskEditingStore, type TaskEditorSeen } from '@/stores/taskEditingStore';
import type { ApiResponse } from '@/lib/api-contract';
import { newerVersion, staleChangeOf, type StaleChange } from '@/lib/stale-write';
import { TASK_EDITING_IDLE_MS, type TaskBoard, type TaskDetail, type TaskUpdateInput, type TaskUpdateResponse } from '@shared/types/tasks';
import { MentionText } from './MentionText';
import { LinksOpenInDialogs } from '@/pages/link-dialog/link-scope';
import { MentionTextarea } from './MentionTextarea';
import { memberName } from './task-chips';
import { TaskEditingNotice } from './TaskEditingNotice';
import { displayToMarkup, markupToDisplay, mentionAdditions, type PickedMention } from './mention-text';
import { useFold } from './use-fold';
import { ExternalLink } from '@/components/ExternalLink';
import { TASK_SOURCE_LABELS } from '@shared/types/task-sources';
import { useIsMobile } from '@/hooks/use-mobile';
import { useVisibleArea } from '@/hooks/use-visual-viewport';

/** `base` is the description's version the edit started from; null before the task has loaded. */
type Draft = { text: string; mentions: PickedMention[]; base: string | null };

/** "Ana", "Ana and Ben", "Ana and 2 others", and whether that's more than one person. */
function names(editors: TaskEditorSeen[], members: TaskBoard['members']): { who: string; many: boolean } {
  const unique = [...new Set(editors.map((e) => e.user_id))].map((id) => memberName(members.find((m) => m.user_id === id)));
  const many = unique.length > 1;
  if (unique.length <= 1) return { who: unique[0] ?? 'Someone', many };
  if (unique.length === 2) return { who: `${unique[0]} and ${unique[1]}`, many };
  return { who: `${unique[0]} and ${unique.length - 1} others`, many };
}

/**
 * Who else has this task's description open to edit (T-46), split into those typing in the last
 * TASK_EDITING_IDLE_MS and those idle longer, and whether one of my own other tabs has it open.
 * Re-renders when someone goes idle.
 */
function useOtherEditors(taskId: string) {
  const me = useAuthStore((s) => s.user?.id);
  const editors = useTaskEditingStore(useShallow((s) => s.editors.filter((e) => e.task_id === taskId)));
  // Now, by the last list's arrival or the last time someone went idle, whichever is later.
  const receivedAt = useTaskEditingStore((s) => s.receivedAt);
  const [tick, setTick] = useState(0);
  const now = Math.max(receivedAt, tick);
  const others = editors.filter((e) => e.user_id !== me);
  const active = others.filter((e) => now - e.active_at < TASK_EDITING_IDLE_MS);
  const nextIdle = Math.min(...active.map((e) => e.active_at + TASK_EDITING_IDLE_MS));
  useEffect(() => {
    if (!Number.isFinite(nextIdle)) return undefined;
    const timer = setTimeout(() => setTick(Date.now()), Math.max(0, nextIdle - Date.now()) + 50);
    return () => clearTimeout(timer);
  }, [nextIdle]);
  return {
    active,
    idle: others.filter((e) => !active.includes(e)),
    myOtherTab: editors.some((e) => e.user_id === me),
  };
}

/**
 * A task's description, shown as markdown (T-9): press it to edit (like the title), saves when you
 * click away, Esc discards. @ mentions assign people and link things when saved.
 *
 * Each save sends the version the edit started from. When someone else saved the
 * description since, nothing is saved: the editor stays open with what was typed and
 * DataChangedBanner offers Reload, which throws the draft away for their version.
 *
 * While someone else has it open to edit (T-46, through the live socket) it says so and won't open
 * the editor; once they haven't typed for TASK_EDITING_IDLE_MS it offers Edit anyway. Your own
 * other tab only gets a note: the stale check above keeps the two apart.
 */
export function TaskDescription({
  task,
  hydrated,
  members,
  saveDescription,
  onReload,
  onNavigate,
}: {
  task: TaskDetail;
  hydrated: boolean;
  members: TaskBoard['members'];
  saveDescription: (
    input: TaskUpdateInput,
    optimistic?: Partial<TaskDetail>
  ) => Promise<ApiResponse<TaskUpdateResponse>>;
  /** Resolves true once their version has arrived. */
  onReload: () => Promise<boolean>;
  onNavigate?: () => void;
}) {
  const [draft, setDraft] = useState<Draft | null>(null);
  // This tab's own last save: its version may land before the task's refetch does.
  const [ownVersion, setOwnVersion] = useState<string | null>(null);
  // This tab's last description save still on its way: the version it started from and the one it
  // made (null if refused). A save sent meanwhile waits for it (tasks-api queues it anyway).
  const lastSave = useRef<Promise<{ from: string | null; to: string | null }> | null>(null);
  const version = newerVersion(task.description_updated_at, ownVersion);
  const [stale, setStale] = useState<StaleChange | null>(null);
  const [reloading, setReloading] = useState(false);
  // A long description folds to a few lines ("Show more"), so what's below stays in view.
  const { foldRef, foldProps, foldable, expanded, toggle } = useFold();
  // Phones (below md, where the task is one column): the editor grows with its text (field-sizing), so a long description made a box many
  // screens tall and the pop-up scrolled to its end, where the cursor goes (T-47). It's kept to half
  // of what you can see above the keyboard and scrolls inside itself instead.
  const mobile = useIsMobile();
  const area = useVisibleArea(draft !== null && mobile);
  const editorStyle = area ? { maxHeight: Math.max(120, Math.round(area.height / 2)) } : undefined;
  // T-46: someone else editing blocks opening the editor; once they've been idle a while, "Edit anyway".
  const editors = useOtherEditors(task.id);
  const othersEditing = [...editors.active, ...editors.idle];
  const blocked = othersEditing.length > 0;
  const editing = draft !== null;
  useEffect(() => {
    if (!editing) return undefined;
    announceEditing(task.id);
    return () => stopEditing(task.id);
  }, [editing, task.id]);

  /** Only a change is sent. `closing`: the task is closing, so nothing here can show a banner. */
  const save = async (closing = false) => {
    if (!draft) return;
    // Someone else's newer save is showing: this draft would be refused again. It stays open
    // until Reload (or Esc); closing the task says it wasn't saved.
    if (stale) {
      if (closing) toast.error("Your description edit wasn't saved", { description: `${stale.changedBy ?? 'Someone'} changed it first.` });
      return;
    }
    const editing = draft;
    const description = displayToMarkup(editing.text, editing.mentions);
    setDraft(null);
    if (description === task.description) return;
    // People @'d for the first time are assigned and things are linked (by the server); show it now.
    const added = mentionAdditions(task.description, description, task, members.map((m) => m.user_id));
    const prior = lastSave.current;
    const sent = (async () => {
      // An edit opened while this tab's previous save was on its way started from that save's text:
      // it goes on the version that save made, or it would be refused as stale against itself.
      const before = prior ? await prior : null;
      const base = before?.to && before.from === editing.base ? before.to : editing.base;
      const res = await saveDescription(
        { description, ...(base ? { expected_updated_at: base } : {}) },
        { description, ...added }
      );
      return { res, base };
    })();
    const outcome = sent.then(
      ({ res, base }) => ({ from: base, to: res.error ? null : res.data?.description_updated_at ?? null }),
      () => ({ from: editing.base, to: null })
    );
    lastSave.current = outcome;
    const { res, base } = await sent;
    if (lastSave.current === outcome) lastSave.current = null;
    if (!res.error) {
      const saved = res.data?.description_updated_at;
      if (saved) {
        setOwnVersion(saved);
        // Same for an edit still open now.
        setDraft((d) => (d && d.base === base ? { ...d, base: saved } : d));
      }
      return;
    }
    // The change is undone; give the text back rather than lose it.
    setDraft(editing);
    const change = staleChangeOf(res);
    if (change) {
      setStale(change);
      if (closing) toast.error("Your description edit wasn't saved", { description: res.error });
    }
  };

  // The task closing mid-edit (before focus leaves the box) still saves what was typed.
  const saveOnClose = useEffectEvent(() => {
    if (draft) void save(true);
  });
  useEffect(() => () => saveOnClose(), []);

  /** `anyway`: Edit anyway, over someone who's gone idle. */
  const startEditing = (anyway = false) => {
    // Until Reload has brought their version, an edit would start from the old one.
    if (reloading) return;
    if (blocked && !(anyway && editors.active.length === 0)) return;
    setDraft({ ...markupToDisplay(task.description), base: hydrated ? version : null });
  };

  /** Their version replaces the draft (the banner says so). A failed reload keeps both. */
  const reload = async () => {
    setReloading(true);
    const loaded = await onReload();
    setReloading(false);
    if (!loaded) {
      toast.error("Couldn't reload the description", { description: 'Your draft is still here. Try Reload again.' });
      return;
    }
    setDraft(null);
    setStale(null);
  };

  const discard = () => {
    setDraft(null);
    setStale(null);
  };

  // A mirrored task's description is changed upstream (T-15): shown here, edited there.
  const upstream = task.upstream;
  const shown = task.description ? (
    <div ref={foldRef} {...foldProps}>
      {/* SF-T-160: its links open in dialogs, as the task's comments do. */}
      <LinksOpenInDialogs>
        <MentionText text={task.description} members={members} contexts={task.contexts} onNavigate={onNavigate} markdown sourceRefs={upstream ? task.source_refs : null} />
      </LinksOpenInDialogs>
    </div>
  ) : null;

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Description</h3>
        {upstream?.url ? (
          <span className="text-xs">
            <ExternalLink href={upstream.url} size="xs">
              Edit in {TASK_SOURCE_LABELS[upstream.kind]}
            </ExternalLink>
          </span>
        ) : null}
      </div>
      {stale ? (
        <DataChangedBanner
          changedBy={stale.changedBy}
          what="the description"
          hasLocalEdits={draft !== null}
          onReload={() => void reload()}
          reloading={reloading}
          onDismiss={() => setStale(null)}
        />
      ) : null}
      {!draft && blocked ? (
        editors.active.length ? (
          <TaskEditingNotice kind="editing" {...names(editors.active, members)} />
        ) : (
          <TaskEditingNotice kind="idle" {...names(editors.idle, members)} onEditAnyway={() => startEditing(true)} />
        )
      ) : null}
      {draft && blocked ? <TaskEditingNotice kind="also" {...names(othersEditing, members)} /> : null}
      {!blocked && editors.myOtherTab ? <TaskEditingNotice kind="own-tab" /> : null}
      {draft ? (
        <div className="space-y-2">
          <MentionTextarea
            aria-label="Description"
            tagging
            value={draft.text}
            onChange={(text) => {
              setDraft((d) => (d ? { ...d, text } : d));
              stillEditing(task.id);
            }}
            mentions={draft.mentions}
            onMentionsChange={(mentions) => setDraft((d) => (d ? { ...d, mentions } : d))}
            members={members}
            // Only comments ask the AI builder (T-21); a description assigns it through Properties.
            aiBuilder={false}
            rows={5}
            // Without visualViewport (older browsers), half the screen.
            className="max-md:max-h-[50dvh]"
            style={editorStyle}
            autoFocus
            placeholder="Add details… @ to mention someone or something, # to tag"
            onSubmit={() => void save()}
            onBlur={() => {
              // Switching to another app or tab isn't leaving the box; it stays open for when you're back.
              if (document.hasFocus()) void save();
            }}
            onEscape={discard}
          />
          <p className="text-xs text-muted-foreground">Saves when you click away · Esc to discard</p>
        </div>
      ) : !hydrated && !task.description ? (
        <div className="space-y-2" aria-label="Loading description">
          <div className="h-3.5 w-4/5 animate-pulse rounded bg-muted" />
          <div className="h-3.5 w-3/5 animate-pulse rounded bg-muted" />
        </div>
      ) : upstream ? (
        <div className="rounded-md border border-input/60 px-3 py-2 text-sm leading-relaxed">
          {shown ?? <span className="text-muted-foreground">No description upstream.</span>}
        </div>
      ) : (
        // Press to edit. Links inside still open, and selecting text to copy doesn't edit.
        <div
          role="button"
          tabIndex={0}
          aria-label="Edit description"
          aria-disabled={blocked || undefined}
          className={cn(
            'rounded-md border border-input/60 px-3 py-2 text-sm leading-relaxed transition-colors focus-visible:border-ring focus-visible:outline-none',
            blocked ? 'cursor-default' : 'cursor-text hover:border-input'
          )}
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('a') || window.getSelection()?.toString()) return;
            startEditing();
          }}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
            e.preventDefault();
            startEditing();
          }}
        >
          {shown ?? <span className="text-muted-foreground">Add more detail…</span>}
        </div>
      )}
      {!draft && task.description && foldable ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full text-muted-foreground"
          onClick={toggle}
        >
          {expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      ) : null}
    </section>
  );
}
